import { Component, OnInit } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { NegotiationChatModalService } from 'src/app/services/negotiation-chat-modal/negotiation-chat-modal.service';
import { MessageService } from 'src/app/services/message/message.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import Swal from 'sweetalert2';
declare var bootstrap: any;

interface ChatMessage {
  ref_id: string;
  message: string;
  attachments: string[];
  type: string;
  user_id: string;
  user: {
    title?: string;
    name?: string;
    country?: string;
    city?: string;
  };
  created_at: string;
  is_read: boolean;
  doc_id: string;
}

interface AttachmentFile {
  file: File;
  uploading: boolean;
  uploadedUrl?: string;
  error?: string;
}

@Component({
  selector: 'app-negotiation-chat',
  templateUrl: './negotiation-chat.component.html',
  styleUrls: ['./negotiation-chat.component.scss']
})
export class NegotiationChatComponent extends DestructorComponent implements OnInit {

  selectedNegotiation: any = null;
  messages: ChatMessage[] = [];
  newMessage: string = '';
  isLoadingMessages: boolean = false;
  isSendingMessage: boolean = false;
  currentUserId: string = '';

  // Attachment handling
  selectedFiles: AttachmentFile[] = [];
  isUploadingAttachments: boolean = false;

  constructor(
    private negotiationChatModalService: NegotiationChatModalService,
    private messageService: MessageService,
    private generalService: GeneralService,
    private globalStateService: GlobalStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.getCurrentUser();
  }

  private getCurrentUser(): void {
    this.subscriptions.push(
      this.globalStateService.user.subscribe(user => {
        if (user && user._id) {
          this.currentUserId = user._id;
        }
      })
    );
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_negotiation_chat');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const negotiation = this.negotiationChatModalService.getSelectedNegotiation();
        if (negotiation) {
          this.selectedNegotiation = negotiation;
          this.loadMessages();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.negotiationChatModalService.clearSelectedNegotiation();
        this.resetChat();
      });
    }
  }

  private resetChat(): void {
    this.selectedNegotiation = null;
    this.messages = [];
    this.newMessage = '';
    this.selectedFiles = [];
    this.isUploadingAttachments = false;
    this.isSendingMessage = false;
  }

  private loadMessages(): void {
    if (!this.selectedNegotiation || !this.selectedNegotiation._id) {
      return;
    }

    this.isLoadingMessages = true;
    this.subscriptions.push(
      this.messageService.GetData('1', '', {
        ref_id: this.selectedNegotiation._id,
        paginate: 'false'
      }).subscribe({
        next: (response: any) => {
          this.isLoadingMessages = false;
          if (response.status && response.data) {
            this.messages = Array.isArray(response.data) ? response.data : [];
            this.scrollToBottom();
          }
        },
        error: (error) => {
          this.isLoadingMessages = false;
          console.error('Error loading messages:', error);
          this.messages = [];
        }
      })
    );
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files).map(file => ({
        file,
        uploading: false
      }));
      this.selectedFiles = [...this.selectedFiles, ...newFiles];

      // Reset input
      input.value = '';
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async sendMessage(): Promise<void> {
    if (!this.newMessage.trim() && this.selectedFiles.length === 0) {
      return;
    }

    if (!this.selectedNegotiation || !this.selectedNegotiation._id) {
      return;
    }

    this.isSendingMessage = true;
    const uploadedUrls: string[] = [];

    try {
      // Upload attachments first if any
      if (this.selectedFiles.length > 0) {
        this.isUploadingAttachments = true;

        for (let i = 0; i < this.selectedFiles.length; i++) {
          const fileItem = this.selectedFiles[i];
          fileItem.uploading = true;

          try {
            const fileUrl = await this.uploadSingleFile(fileItem.file);
            fileItem.uploadedUrl = fileUrl;
            uploadedUrls.push(fileUrl);
          } catch (error) {
            fileItem.error = 'Upload failed';
            console.error('Error uploading file:', error);
          } finally {
            fileItem.uploading = false;
          }
        }

        this.isUploadingAttachments = false;
      }

      // Send message with attachments
      const messageData = {
        message: this.newMessage.trim(),
        type: 'negotiation',
        attachments: uploadedUrls,
        ref_id: this.selectedNegotiation._id
      };

      this.subscriptions.push(
        this.messageService.AddData(messageData).subscribe({
          next: (response: any) => {
            this.isSendingMessage = false;
            if (response.status) {
              // Clear form
              this.newMessage = '';
              this.selectedFiles = [];

              // Reload messages
              this.loadMessages();
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: response.message || 'Failed to send message'
              });
            }
          },
          error: (error) => {
            this.isSendingMessage = false;
            console.error('Error sending message:', error);
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'An error occurred while sending the message'
            });
          }
        })
      );
    } catch (error) {
      this.isSendingMessage = false;
      this.isUploadingAttachments = false;
      console.error('Error in sendMessage:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while processing your message'
      });
    }
  }

  private uploadSingleFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this.generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
        next: (res) => {
          const fileUrl = res.data.url;
          const formData = this.generalService.convertKeysToFormdata(res.data.result.fields, file);

          this.generalService.sendFileToS3(res.data.result.url, formData).subscribe({
            next: () => {
              resolve(fileUrl);
            },
            error: (error) => {
              reject(error);
            }
          });
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  isMyMessage(message: ChatMessage): boolean {
    return message.user_id === this.currentUserId;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const messagesContainer = document.querySelector('.chat-messages');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_negotiation_chat');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }
}
